// ONE RUBRIC FRAME, used twice.
//
// A rubric is a table — traits down, levels across, a descriptor in every cell — and that is how
// teachers read one on paper. This is that table, and it renders in both places a teacher needs
// it: on the lesson, where it is the instrument on its own, and over a student's work, where the
// awarded cell is filled in and the rest recede.
//
// The point of sharing it is that the second reading inherits the first. A teacher who has read
// the rubric on the lesson page recognises the same grid with one cell lit, and the score is a
// position in a rubric rather than a number with prose under it.
//
// WHAT A CELL CLICK IS, and what it is not. Clicking a cell SELECTS it: the passages that
// criterion was scored on light up in the paper, and a box below the table names the cell and
// gives the model's reason. It does not change the score. Changing the score is a separate,
// labelled act in that box, because a table where clicking a cell silently re-scored a student
// would make every misclick a judgment.

export interface RubricLevel {
  level: number;
  descriptor: string | string[] | null;
  stacked?: boolean;
}

export interface RubricRow {
  trait_id: string;
  label: string | null;
  standard?: string | null;
  levels: RubricLevel[];
  /** False when no published version of this trait exists: the scorer skips it, and the row says
   *  so rather than being dropped into an invisible gap. */
  published?: boolean;
}

/** What a student was given on a trait. Absent on the lesson page, where there is no student. */
export interface Awarded {
  level: number | null;
  /** `scored`, or an outcome that carries no number — abstained, no_verified_evidence. */
  status: string;
  /** True when a teacher's override is the standing score rather than the machine's. */
  overridden?: boolean;
}

export interface CellRef {
  trait_id: string;
  level: number;
}

export function RubricTable({ rows, awarded, selected, onSelect }: {
  rows: RubricRow[];
  awarded?: Map<string, Awarded>;
  selected?: CellRef | null;
  onSelect?: (cell: CellRef) => void;
}) {
  // The widest scale, because traits do not have to share one: PERSUADE's holistic trait runs
  // 1-6 and its elements run 1-3. A row shorter than the header leaves its trailing cells empty
  // rather than stretching, so two different scales cannot look like one.
  const widest = rows.reduce<number[]>(
    (best, r) => (r.levels.length > best.length ? r.levels.map((l) => l.level) : best), []);
  const clickable = Boolean(onSelect);

  return (
    <table className={`rt${clickable ? " rt-live" : ""}`}>
      <thead>
        <tr>
          <th className="rt-trait">Trait</th>
          {widest.map((l) => <th key={l}>{l}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const got = awarded?.get(r.trait_id);
          return (
            <tr key={r.trait_id} className={got?.status && got.status !== "scored" ? "rt-nolevel" : ""}>
              <th className="rt-trait" scope="row">
                <span className="rt-label">{r.label ?? r.trait_id}</span>
                {r.standard && <span className="rt-standard">{r.standard}</span>}
                {r.published === false && (
                  <span className="rt-draft">not published — not scored</span>
                )}
              </th>
              {/* An outcome with no number is not a row of blank cells. It sits where the levels
                  would be and says what happened instead, because "we could not tell" and "this
                  is weak" are different findings the whole record is built to keep apart. */}
              {got && got.status !== "scored" ? (
                <td className="rt-verdict" colSpan={widest.length}>
                  {NO_NUMBER[got.status] ?? got.status}
                </td>
              ) : widest.map((level, i) => {
                const cell = r.levels[i];
                if (!cell) return <td key={level} className="rt-off" aria-hidden="true" />;
                const isAwarded = got?.status === "scored" && got.level === cell.level;
                const isSelected = selected?.trait_id === r.trait_id
                                && selected?.level === cell.level;
                const classes = [
                  isAwarded ? "rt-awarded" : "",
                  isSelected ? "rt-selected" : "",
                  awarded && !isAwarded ? "rt-dim" : "",
                ].filter(Boolean).join(" ");
                const body = (
                  <>
                    {/* No level number in the cell. It is the column heading, repeated down
                        every row was a column of 1s beside a column of 2s. */}
                    {Array.isArray(cell.descriptor)
                      // A stacked cell stays a list of clauses: the linter flags those because
                      // two raters can score the same paper on different ones.
                      ? <ul className="rt-stacked">
                          {cell.descriptor.map((clause, j) => <li key={j}>{clause}</li>)}
                        </ul>
                      : <span className="rt-text">
                          {cell.descriptor ?? <i className="rt-none">no wording</i>}
                        </span>}
                  </>
                );
                return (
                  <td key={cell.level} className={classes}>
                    {clickable
                      ? <button type="button"
                                aria-pressed={isSelected}
                                onClick={() => onSelect?.({ trait_id: r.trait_id, level: cell.level })}>
                          {body}
                        </button>
                      : body}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/** A criterion outcome that carries no number, said in words rather than left as an empty cell. */
const NO_NUMBER: Record<string, string> = {
  abstained: "Could not place it — needs you",
  no_verified_evidence: "No verified evidence — needs you",
  not_attempted: "Not attempted",
  skipped: "Not scored",
};
